import React, {useEffect, useState} from "react";
import Layout from "../components/layout/Layout";
import Breadcrumb from "../components/layout/Breadcrumb";
import Section from "../components/layout/Section";
import VacatureCard from "../components/vacatures/VacatureCard";
import Pagination from "../components/vacatures/Pagination";
import TotalAmount from "../components/vacatures/TotalAmount";
import SectorFilter from "../components/vacatures/SectorFilter";
import LocationFilter from "../components/vacatures/LocationFilter";
import {VacatureModel} from "./api/vacature.model";
import Head from "next/head";
import VakgebiedFilter from "../components/vacatures/VakgebiedFilter";
import Link from "next/link";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronRight} from "@fortawesome/free-solid-svg-icons/faChevronRight";
import {useGlobalContext} from "../providers/global.provider";

const PAGE_SIZE = 6;
const SORT_BY = ["Nieuwste eerst", "Oudste eerst"];

function PageVacatures() {

    const {isLoading, error, vacatures} = useGlobalContext();

    const [filteredVacatures, setFilteredVacatures] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [locations, setLocations] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [vakgebieden, setVakgebieden] = useState([]);
    const [activeSectors, setActiveSectors] = useState([]);
    const [activeVakgebieden, setActiveVakgebieden] = useState([]);
    const [activeLocations, setActiveLocation] = useState([]);
    const [sortBy, setSortBy] = useState(SORT_BY[0]);

    useEffect(() => {
        afterFetch(vacatures);
    }, [vacatures]);

    const afterFetch = (data: VacatureModel[]) => {

        data = data.sort((a, b) => {
            const dateA = new Date(a.publish_date);
            const dateB = new Date(b.publish_date);
            // @ts-ignore
            return dateB - dateA;
        });

        setFilteredVacatures(data);

        setTotalPages(Math.ceil(data.length / PAGE_SIZE));
        // @ts-ignore
        setLocations([...new Set(data.map((item) => item.location))]);
        // @ts-ignore
        setSectors([...new Set(data.map((item) => item.sector))]);
        // @ts-ignore
        setVakgebieden([...new Set(data.map((item) => item.vakgebied))]);
    }

    const filterChanged = (sectors: string[], locations: string[], vakgebieden: string[]) => {
        if (sectors !== activeSectors)
            setActiveSectors(sectors);
        if (locations !== activeLocations)
            setActiveLocation(locations);
        if (vakgebieden !== activeVakgebieden)
            setActiveVakgebieden(vakgebieden);
        let vs = [...vacatures];
        if (sectors.length > 0) {
            vs = vs.filter((v) => sectors.includes(v.sector));
        }
        if (locations.length > 0) {
            vs = vs.filter((v) => locations.includes(v.location));
        }
        if (vakgebieden.length > 0) {
            vs = vs.filter((v) => vakgebieden.includes(v.vakgebied));
        }
        vs = getSorted(vs, sortBy);
        setFilteredVacatures(vs);
        setTotalPages(Math.ceil(vs.length / PAGE_SIZE));
        resetToFirstPage();
    }

    const getSorted = (values, sort) => {
        let vs = [...values];
        switch (sort) {
            case "Nieuwste eerst":
                vs = vs.sort((a, b) => {
                    const dateA = new Date(a.publish_date);
                    const dateB = new Date(b.publish_date);
                    // @ts-ignore
                    return dateB - dateA;
                });
                break;
            case "Oudste eerst":
                vs = vs.sort((a, b) => {
                    const dateA = new Date(a.publish_date);
                    const dateB = new Date(b.publish_date);
                    // @ts-ignore
                    return dateA - dateB;
                });
                break;
        }
        return vs;
    }

    const sortChanged = sortBy => {
        setSortBy(sortBy);
        resetToFirstPage();
        setFilteredVacatures(getSorted(filteredVacatures, sortBy));
    }

    const resetToFirstPage = () => {
        if (currentPage !== 1)
            setCurrentPage(1);
    }

    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;

    return (
        <>
            <Head>
                <link rel="manifest" href="/manifest.webmanifest"/>
                <link rel="apple-touch-icon" href="/assets/imgs/app/apple-touch-icon.png"/>
                <link
                    rel="apple-touch-icon"
                    sizes="48x48"
                    href="/assets/imgs/app/apple-touch-icon-48x48.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="57x57"
                    href="/assets/imgs/app/apple-touch-icon-57x57.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="72x72"
                    href="/assets/imgs/app/apple-touch-icon-72x72.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="76x76"
                    href="/assets/imgs/app/apple-touch-icon-76x76.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="114x114"
                    href="/assets/imgs/app/apple-touch-icon-114x114.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="120x120"
                    href="/assets/imgs/app/apple-touch-icon-120x120.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="144x144"
                    href="/assets/imgs/app/apple-touch-icon-144x144.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="152x152"
                    href="/assets/imgs/app/apple-touch-icon-152x152.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href="/assets/imgs/app/apple-touch-icon-180x180.png"
                />
                <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5"/>
                <meta charSet="utf-8"/>
                <meta name="msapplication-TileColor" content="#ffffff"/>
                <meta name="msapplication-TileImage" content="/assets/imgs/ms-icon-144x144.png"/>
                <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
                <meta name="viewport" content="width=device-width, initial-scale=1"/>
                <meta name="author" content="Cohesian"/>
                <meta name="theme-color" content="#ffffff"/>
                <meta name="description"
                      content="Laat ons weten waar je naar op zoek bent, zodat wij mee kunnen helpen in je zoektocht!"/>
                <meta name="keywords"
                      content="Engineering, Consultancy/consulting, Entrepreneurship, Entrepreneur, Ondernemer, Ondernemerschap, Technische profielen, Ingenieur, Innovatie, Fun, Connectie, Connection, Excellence, Excellentie, Ingenieursbureau, Partner, Persoonlijke ontwikkeling, Projectsourcing, Recruitment, Rekrutering, Training, Begeleiding, advies, Vacature, Jobs, Werk, Functie, Fun factor, Koffie, Netwerken, Maakindustrie, Automotive, Farmaceutica, Cosmetica"/>
                <meta property="og:title" content="Cohesian Engineering"/>
                <meta property="og:description"
                      content="Visit our website for updates and information about Cohesian Engineering."/>
                <meta property="og:url" content="https://www.cohesian.be/"/>
                <title>Cohesian | Vacatures</title>
            </Head>
            <Layout>
                <Breadcrumb title="Vacatures" url="/Vacatures"/>
                <Section>
                    <div className="container">
                        <div className="row dr-rtl">
                            <div
                                className="col-xl-9 col-lg-8 col-md-12 col-sm-12 col-12 dr-ltr order-xl-1 order-lg-1 order-md-2 order-sm-2 order-2">
                                <TotalAmount vacatures={filteredVacatures} sortBy={sortBy} sortChanged={sortChanged}
                                             sortableBy={SORT_BY}/>
                                <div className="row mt-20">
                                    {isLoading ? (
                                        <div className="text-center">
                                            <div className="spinner-border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : error ? (
                                        <div className="alert alert-danger" role="alert">
                                            {error}
                                        </div>
                                    ) : (
                                        filteredVacatures.slice(startIndex, endIndex).map((item) => <VacatureCard
                                            item={item} key={item.id}/>)
                                    )}
                                </div>
                                <Pagination
                                    setCurrentPage={setCurrentPage}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    vacatures={filteredVacatures}/>
                            </div>
                            <div
                                className="col-xl-3 col-lg-4 col-md-12 col-sm-12 col-12 dr-ltr order-xl-2 order-lg-2 order-md-1 order-sm-1 order-1">
                                <div className="filter-border">
                                    <div className="widget-title mb-30">
                                        <h3 className="text-heading-5 color-gray-900">Filter vacatures</h3>
                                    </div>
                                    <div>
                                        <VakgebiedFilter
                                            setFilters={(v) => filterChanged(activeSectors, activeLocations, v)}
                                            activeFilters={activeVakgebieden}
                                            vakgebieden={vakgebieden}/>
                                        <div className='mt-10'/>
                                        <SectorFilter
                                            setFilters={(s) => filterChanged(s, activeLocations, activeVakgebieden)}
                                            activeFilters={activeSectors}
                                            sectors={sectors}/>
                                        <div className='mt-10'/>
                                        <LocationFilter
                                            setFilters={(l) => filterChanged(activeSectors, l, activeVakgebieden)}
                                            activeFilters={activeLocations}
                                            locations={locations}/>
                                    </div>
                                </div>

                                <div className="bg-gray-100 p-30 bdr-16 d-none d-xl-block">
                                    <h3 className="text-heading-5 color-gray-900">Spontane sollicitatie</h3>
                                    <p className="text-body-text color-gray-500 pt-20">
                                        Is dit niet jouw droomjob? Laat ons weten waar je naar op zoek bent, zodat wij
                                        mee kunnen helpen in je zoektocht!
                                    </p>
                                    <Link href="/Spontaan" legacyBehavior>
                                        <a className="hover-up btn btn-green-900 btn-green-small mt-20">
                                            Solliciteren <FontAwesomeIcon className="font-xs"
                                                                          icon={faChevronRight}/>
                                        </a>
                                    </Link>
                                </div>
                            </div>
                            <div className="col-12 text-start order-3 spontaan-vacature">
                                <div className="bg-gray-100 p-20 bdr-16">
                                    <h3 className="text-heading-5 color-gray-900">Spontane sollicitatie</h3>
                                    <p className="text-body-text color-gray-500">
                                        Is dit niet jouw droomjob? Laat ons weten waar je naar op zoek bent, zodat wij
                                        mee kunnen helpen in je zoektocht
                                    </p>
                                    <Link href="/Spontaan" legacyBehavior>
                                        <a className="hover-up btn btn-green-900 btn-green-small mt-20">
                                            <FontAwesomeIcon className="font-xs" icon={faChevronRight}/> Solliciteren
                                        </a>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>
            </Layout>
        </>
    );
};

export default PageVacatures;
